import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

/**
 * UI Store State Interface - manages UI-only state that doesn't need display window synchronization
 * @interface UIState
 */
interface UIState {
	// UI-only state (not synced to display window)
	/** Current lyrics input text for song creation */
	lyricsInput: string
	/** ID of the currently selected song, null if none selected */
	currentSongId: string | null

	// Modal states
	/** Whether the song picker modal is visible */
	showSongPicker: boolean
	/** Whether the help modal is visible */
	showHelpModal: boolean
	/** Whether the gospel symbols modal is visible */
	showGospelSymbolsModal: boolean
	/** Whether the about modal is visible */
	showAboutModal: boolean
	/** Whether the emoji dictionary modal is visible */
	showEmojiDictionaryModal: boolean
}

/**
 * UI Store Actions Interface - defines all actions for managing UI state
 * @interface UIActions
 */
interface UIActions {
	// Input actions
	/**
	 * Set the lyrics input text
	 * @param input - New lyrics text
	 */
	setLyricsInput: (input: string) => void

	/**
	 * Set the current song ID
	 * @param id - Song ID to set as current, or null to clear
	 */
	setCurrentSongId: (id: string | null) => void

	// Modal actions
	/** Open the song picker modal */
	openSongPicker: () => void
	/** Close the song picker modal */
	closeSongPicker: () => void
	/** Open the help modal */
	openHelpModal: () => void
	/** Close the help modal */
	closeHelpModal: () => void
	/** Open the gospel symbols modal */
	openGospelSymbolsModal: () => void
	/** Close the gospel symbols modal */
	closeGospelSymbolsModal: () => void
	/** Open the about modal */
	openAboutModal: () => void
	/** Close the about modal */
	closeAboutModal: () => void
	/** Open the emoji dictionary modal */
	openEmojiDictionaryModal: () => void
	/** Close the emoji dictionary modal */
	closeEmojiDictionaryModal: () => void
}

/**
 * Combined UI Store Interface - merges state and actions for the complete store interface
 * This store manages all UI-only state that doesn't require synchronization with the display window.
 *
 * @interface UIStore
 * @extends UIState
 * @extends UIActions
 */
export interface UIStore extends UIState, UIActions {}

/**
 * Zustand store for managing UI-only state in the Memtastic application.
 * This store handles modal visibility, input state, and other UI elements that don't
 * need to be synchronized with the display window.
 *
 * Features:
 * - Modal state management (song picker, help, gospel symbols)
 * - Lyrics input state for song creation
 * - Current song selection tracking
 * - Devtools integration for debugging
 *
 * @returns UIStore hook for accessing UI state and actions
 */
export const useUIStore = create<UIStore>()(
	devtools(
		(set) => ({
			// Initial state
			lyricsInput: '',
			currentSongId: null,
			showSongPicker: false,
			showHelpModal: false,
			showGospelSymbolsModal: false,
			showAboutModal: false,
			showEmojiDictionaryModal: false,

			// Input actions
			setLyricsInput: (input: string) => {
				set({ lyricsInput: input })
			},

			setCurrentSongId: (id: string | null) => {
				set({ currentSongId: id })
			},

			// Modal actions
			openSongPicker: () => {
				set({ showSongPicker: true })
			},

			closeSongPicker: () => {
				set({ showSongPicker: false })
			},

			openHelpModal: () => {
				set({ showHelpModal: true })
			},

			closeHelpModal: () => {
				set({ showHelpModal: false })
			},

			openGospelSymbolsModal: () => {
				set({ showGospelSymbolsModal: true })
			},

			closeGospelSymbolsModal: () => {
				set({ showGospelSymbolsModal: false })
			},

			openAboutModal: () => {
				set({ showAboutModal: true })
			},

			closeAboutModal: () => {
				set({ showAboutModal: false })
			},

			openEmojiDictionaryModal: () => {
				set({ showEmojiDictionaryModal: true })
			},

			closeEmojiDictionaryModal: () => {
				set({ showEmojiDictionaryModal: false })
			},
		}),
		{ name: 'ui-store' },
	),
)
